import { BaseLogger } from '../BaseLogger';
import { BACKGROUND_EVENTS, COMPARISON_IDS_KEY_FOR_ADOBE, DOWNLOAD_ACTION_MATCH_TERMS } from '../constants';
import { Logger } from '../Logger';
import { AppMetadata, EventType, LINK_TYPE, LinkEvent, TrackingData } from '../models';
import {
  getLinkName,
  loadAdobeAnalyticsScript,
  removePortfolioTypeFromComparisonIds,
  setAnalyticsParamByKey,
  setAnalyticsParams,
  utagLink,
} from '../utils/AdobeAnalyticsUtils';

export class AdobeLogger implements BaseLogger {
  private _appMetadata: AppMetadata = {};
  private static adobeScriptLoaded = false;

  /**
   * This function initializes adobe analytics scripts
   * @param appMetadata AppMetadata
   */
  async initLogger(appMetadata: AppMetadata): Promise<void> {
    this._appMetadata = appMetadata;
    setAnalyticsParams(this._appMetadata);
    if (!AdobeLogger.adobeScriptLoaded) {
      AdobeLogger.adobeScriptLoaded = true;
      await loadAdobeAnalyticsScript(this._appMetadata); // Ensure async compatibility
      document.addEventListener('click', Logger.declarativeTrackingHandler);
    }
    return Promise.resolve();
  }

  /**
   * This function translates the tracking data to utag link event
   * @param trackingData TrackingData
   */
  trackEvent(trackingData: TrackingData): void {
    // Support specific event types for Adobe Analytics
    const supportedEventTypes = [EventType.EVENT_CLICK, EventType.EVENT_CHANGE, EventType.EVENT_SUBMIT];

    const eventType = trackingData.elementInfo?.eventType ?? EventType.EVENT_CLICK;

    // Check if the event type is supported - type-safe check
    if (
      supportedEventTypes.includes(eventType as (typeof supportedEventTypes)[number]) &&
      !BACKGROUND_EVENTS.includes(trackingData.action)
    ) {
      // Set portfolioId for Adobe from comparisonIds if available
      setAnalyticsParamByKey(
        'portfolioIds',
        removePortfolioTypeFromComparisonIds(
          trackingData.domainSpecificInfo?.additionalAttributes?.[COMPARISON_IDS_KEY_FOR_ADOBE] || ''
        )
      );
      const linkName = getLinkName(trackingData, this._appMetadata);
      if (!!linkName) {
        const linkObj: LinkEvent = {
          linkType: this.determineLinkType(linkName),
          link_name: linkName,
        };
        utagLink(linkObj);
      }
    }
  }

  /**
   * Determine link type from link name
   */
  private determineLinkType(linkName: string): LINK_TYPE {
    // Download actions
    if (DOWNLOAD_ACTION_MATCH_TERMS.some((str) => linkName.includes(str))) {
      return LINK_TYPE.DOWNLOAD;
    }
    // Default to general clicks and custom events
    return LINK_TYPE.GENERAL;
  }
}
